import * as React from 'react';

import type { Block, Inline, Leaf, Mark, Text, TextRange } from '@gitbook/slate';
import type { Set as ISet, List } from '@gitbook/util-immutable';

import type { EditorContainer, MarkProps, SelectionProps } from '../interfaces';
import { OffsetKey } from '../utils';

interface LeafRendererProps {
    editor: EditorContainer;
    block: Block;
    node: Text;
    index: number;
    offset: number;
    text: string;
    leaves: List<Leaf>;
    marks: ISet<Mark>;
    selections: ISet<TextRange>;
    parent: Block | Inline;
}

/*
 * Render a leaf of text with all of its marks.
 */
const LeafRenderer = React.memo(function MemoLeafRenderer(
    props: LeafRendererProps
): React.ReactElement {
    const { marks, selections, node, index, offset, text, editor } = props;
    const { stack } = editor;
    const leaf = renderLeafText(props);

    const offsetKey = OffsetKey.stringify({
        key: node.key,
        index,
    });

    // Optimization: we pass the offset attributes to at least one DOM element rendered for this leaf
    // if there are marks, the first DOM element for marks will have the offsetAttributes; otherwise a span will wrap the text
    let offsetAttributes: object | null = {
        'data-offset-key': offsetKey,
    };

    // Render marks
    const contentWithMarks = marks.reduce((children: React.ReactNode, mark: Mark) => {
        const markProps: MarkProps = {
            editor,
            mark,
            marks,
            node,
            offset,
            text,
            children,
            attributes: {
                'data-slate-leaf': true,
                ...(offsetAttributes || {}),
            },
        };

        offsetAttributes = null;

        const element = stack.find('renderMark', markProps);
        return element || children;
    }, leaf);

    // Render selections
    const contentWithSelections = selections.reduce(
        (children: React.ReactNode, selection: TextRange) => {
            const selectionProps: SelectionProps = {
                type: 'range',
                selection,
                children,
            };
            const element = stack.find('renderSelection', selectionProps);
            return element || children;
        },
        contentWithMarks
    );

    return offsetAttributes ? (
        <span {...offsetAttributes}>{contentWithSelections}</span>
    ) : (
        contentWithSelections
    );
}, areEqual);

type LeafTextProps = Pick<LeafRendererProps, 'node' | 'text' | 'index' | 'parent' | 'leaves'> & {
    block: Block | Inline;
    placeholder?: string;
    horizontalAlignment?: 'left' | 'center' | 'right';
};

/**
 * Render the actual text of the leaf. Exported so that it can be used by virtualization
 * or other modules wishing to get an exact representation of the text.
 */
export function renderLeafText(props: LeafTextProps): React.ReactNode {
    const { block, node, text, index, parent, leaves, placeholder, horizontalAlignment } = props;

    // COMPAT: Render text inside void nodes with a zero-width space.
    // So the node can contain selection but the text is not visible.
    if (parent.isVoid) {
        return <span data-slate-zero-width="z">{'\u200B'}</span>;
    }

    const isLastTextNodeInEmptyBlock =
        text === '' && parent.object === 'block' && parent.text === '' && parent.nodes.size === 1;

    // COMPAT: If this is the last text node in an empty block, render a zero-
    // width space that will convert into a line break when copying and pasting
    // to support expected plain text.
    if (isLastTextNodeInEmptyBlock) {
        if (placeholder) {
            return (
                <span data-slate-zero-width="n">
                    <span
                        className={`pointer-events-none absolute inline-block max-w-full select-none overflow-hidden text-ellipsis whitespace-nowrap opacity-50 ${
                            horizontalAlignment === 'left'
                                ? 'left-0'
                                : horizontalAlignment === 'center'
                                  ? 'right-0 left-0'
                                  : 'right-0'
                        }`}
                    >
                        {placeholder}
                    </span>
                    {'\u200B'}
                </span>
            );
        }

        return <span data-slate-zero-width="n">{'\u200B'}</span>;
    }

    // COMPAT: If the text is empty, it's because it's on the edge of an inline
    // void node, so we render a zero-width space so that the selection can be
    // inserted next to it still.
    if (text === '') {
        return <span data-slate-zero-width="z">{'\u200B'}</span>;
    }

    // COMPAT: Browsers will collapse trailing new lines at the end of blocks,
    // so we need to add an extra trailing new lines to prevent that.
    const lastText = block.getLastText();
    const lastChar = text.charAt(text.length - 1);
    const isLastText = node === lastText;
    const isLastLeaf = index === leaves.size - 1;
    if (isLastText && isLastLeaf && lastChar === '\n') {
        return `${text}\n`;
    }

    // Otherwise, just return the text.
    return text;
}

/*
 * Should we render the leaf ?
 */
function areEqual(props: LeafRendererProps, nextProps: LeafRendererProps): boolean {
    // If any of the regular properties have changed, re-render.
    if (
        nextProps.index !== props.index ||
        nextProps.marks !== props.marks ||
        nextProps.selections !== props.selections ||
        nextProps.parent !== props.parent ||
        nextProps.text !== props.text
    ) {
        return false;
    }

    // Otherwise, don't update.
    return true;
}

export default LeafRenderer;
